import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap, catchError } from 'rxjs';

export interface ViewColumnConfig {
  id: number;        // viewConfigId — used when saving
  key: string;        // matches the *ngIf key used in table templates
  header: string;
  visible: boolean;
  locked: boolean;
}

export interface ModuleViewConfig {
  moduleName: string;  // 'Orders' | 'Customers' | 'Customer Contracts' ...
  columns: ViewColumnConfig[];
}

const STORAGE_KEY = 'viewConfiguration';
const GET_URL   = 'https://ftl-dev.apollosupplychain.com/account-service/user/view-configuration';
const SAVE_URL  = 'https://ftl-dev.apollosupplychain.com/account-service/user/viewPreference/addOrUpdate';

@Injectable({ providedIn: 'root' })
export class ColumnPreferenceService {

  constructor(private http: HttpClient) {}

  private get userId(): number {
    const user = JSON.parse(localStorage.getItem('UserDetails') || '{}');
    return user?.id;
  }

  /** Call ONCE, right after login (after UserDetails is stored). */
  loadAndCacheConfig(userId:any): Observable<ModuleViewConfig[]> {
    return this.http.get<any>(`${GET_URL}?userId=${userId}`).pipe(
      tap(res => {
        const modules: ModuleViewConfig[] = res?.responseObject || [];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(modules));
      }),
      catchError(err => {
        console.error('Failed to load column configuration', err);
        return of([]);
      })
    );
  }

  /** Synchronous read — safe to call from templates/getters, no HTTP round-trip. */
  getModuleColumns(moduleName: string): ViewColumnConfig[] {
    const all: ModuleViewConfig[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return all.find(m => m.moduleName === moduleName)?.columns || [];
  }

  isVisible(moduleName: string, key: string): boolean {
    const col = this.getModuleColumns(moduleName).find(c => c.key === key);
    return col ? col.visible : true; // default visible if backend hasn't configured this key yet
  }

  /** Fired on every checkbox click. Optimistic update, rolled back on API failure. */
  saveColumnVisibility(moduleName: string, column: ViewColumnConfig): Observable<any> {
    this.patchLocalVisibility(moduleName, column.key, column.visible);
  
    const payload = {
      userId: this.userId,
      viewConfigId: column.id,
      visible: column.visible
    };
  
    return this.http.post(SAVE_URL, payload);
  }

  private patchLocalVisibility(moduleName: string, key: string, visible: boolean): void {
    const all: ModuleViewConfig[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const mod = all.find(m => m.moduleName === moduleName);
    const col = mod?.columns.find(c => c.key === key);
    if (col) col.visible = visible;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  }
}